import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, inject, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { AuthService, OAuthTokenUser } from '../../core/auth/auth.service';
import { authRetryAfterSeconds } from '../../core/auth/auth-rate-limit.util';
import { UserContextService } from '../../core/auth/user-context.service';
import { InMemoryDataService } from '../../core/data/in-memory-data.service';
import { LocalizationService } from '../../core/i18n/localization.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, TranslatePipe],
  template: `
    <main class="login-page">
      <mat-card class="login-card">
        <mat-card-content>
          <div class="brand">
            <div class="brand-mark">OS</div>
            <div>
              <h1>OS Portal</h1>
              <p>{{ 'login.subtitle' | t }}</p>
            </div>
          </div>

          <form [formGroup]="form" (ngSubmit)="submit()">
            <mat-form-field>
              <mat-label>{{ 'login.username' | t }}</mat-label>
              <input matInput autocomplete="username" formControlName="username" />
            </mat-form-field>

            <mat-form-field>
              <mat-label>{{ 'login.password' | t }}</mat-label>
              <input matInput type="password" autocomplete="current-password" formControlName="password" />
            </mat-form-field>

            @if (errorMessage()) {
              <div class="login-error">{{ errorMessage() }}</div>
            }

            <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || loading() || retrySeconds() > 0">
              @if (loading()) {
                <mat-spinner diameter="20" />
              } @else {
                <mat-icon>login</mat-icon>
              }
              @if (retrySeconds() > 0) { {{ 'oauth.passwordCompletion.retryIn' | t: { seconds: retrySeconds() } }} } @else { {{ 'login.submit' | t }} }
            </button>
            <button mat-button type="button" class="reset-toggle" (click)="openPasswordResetDialog()">
              {{ 'login.forgotPassword' | t }}
            </button>
          </form>
        </mat-card-content>
      </mat-card>
    </main>
  `,
  styles: `
    :host {
      display: block;
      min-height: 100dvh;
      background:
        linear-gradient(90deg, rgba(248, 250, 252, 0.88), rgba(248, 250, 252, 0.62)),
        url('/assets/login-background.svg') center / cover no-repeat;
    }

    .login-page {
      min-height: 100dvh;
      display: grid;
      place-items: center;
      padding: 24px;
      background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0));
    }

    .login-card {
      width: min(440px, 100%);
      border-color: rgba(203, 213, 225, 0.72);
      background: rgba(255, 255, 255, 0.92) !important;
      box-shadow: 0 24px 70px rgba(15, 23, 42, 0.16) !important;
      backdrop-filter: blur(10px);
    }

    .brand {
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 24px;
    }

    .brand-mark {
      display: grid;
      place-items: center;
      width: 48px;
      height: 48px;
      border-radius: 8px;
      background: var(--app-accent);
      color: #ffffff;
      font-weight: 900;
    }

    h1 {
      margin: 0;
      font-size: 1.8rem;
      line-height: 1.1;
    }

    p {
      margin: 4px 0 0;
      color: var(--app-text-muted);
    }

    form {
      display: grid;
      gap: 12px;
    }

    mat-form-field {
      width: 100%;
    }

    button[type='submit'] {
      height: 48px;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .reset-toggle {
      justify-self: center;
      color: var(--app-accent);
      font-weight: 800;
    }

    .login-error {
      padding: 10px 12px;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #b91c1c;
      background: #fef2f2;
      font-weight: 700;
    }
  `
})
export class LoginPageComponent implements OnDestroy {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly auth = inject(AuthService);
  private readonly userContext = inject(UserContextService);
  private readonly dataService = inject(InMemoryDataService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(false);
  readonly errorMessage = signal('');
  readonly retrySeconds = signal(0);
  private retryTimer: ReturnType<typeof setInterval> | null = null;
  readonly form = this.fb.group({
    username: this.fb.control('', Validators.required),
    password: this.fb.control('', Validators.required)
  });

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading()) {
      return;
    }
    this.loading.set(true);
    this.errorMessage.set('');
    this.auth.clearSession();
    this.userContext.clearAuthenticatedUser();
    this.dataService.clearSessionData();

    try {
      const { username, password } = this.form.getRawValue();
      const session = await this.auth.login(username, password);
      this.userContext.setAuthenticatedUser(session.user);
      const loaded = await this.dataService.reload();
      if (!loaded) {
        this.auth.clearSession();
        this.userContext.clearAuthenticatedUser();
        this.dataService.clearSessionData();
        this.errorMessage.set(this.localization.translate('login.dataLoadFailed'));
        return;
      }
      await this.router.navigate([this.redirectFor(session.user)]);
    } catch (error) {
      const retryAfter = authRetryAfterSeconds(error);
      if (retryAfter) {
        this.startRetryCountdown(retryAfter);
        this.errorMessage.set(this.localization.translate('auth.rateLimited', { seconds: retryAfter }));
      } else if (error instanceof HttpErrorResponse && error.status === 401) {
        this.errorMessage.set(this.localization.translate('api.error.authenticationRequired'));
      } else if (error instanceof HttpErrorResponse && error.status === 403) {
        this.errorMessage.set(this.localization.translate('api.error.accessDenied'));
      } else {
        this.errorMessage.set(this.localization.translate('login.loginFailed'));
      }
    } finally {
      this.loading.set(false);
    }
  }

  ngOnDestroy(): void { if (this.retryTimer) clearInterval(this.retryTimer); }

  private startRetryCountdown(seconds: number): void {
    if (this.retryTimer) clearInterval(this.retryTimer);
    this.retrySeconds.set(seconds);
    this.retryTimer = setInterval(() => {
      this.retrySeconds.update((value) => Math.max(0, value - 1));
      if (this.retrySeconds() === 0 && this.retryTimer) { clearInterval(this.retryTimer); this.retryTimer = null; }
    }, 1000);
  }

  openPasswordResetDialog(): void {
    this.dialog.open(PasswordResetDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)'
    });
  }

  private redirectFor(user: OAuthTokenUser): string {
    if (
      user.role === 'admin' &&
      this.isGlobalOrganisationContext(user.organisationId) &&
      this.isGlobalOrganisationContext(user.requestedOrganisationId) &&
      this.isGlobalOrganisationContext(user.requestingOrganisationId)
    ) {
      return '/admin';
    }
    if (user.role === 'instructor') {
      return '/instructor';
    }
    if (user.role === 'student') {
      return '/student';
    }
    return '/dashboard';
  }

  private isGlobalOrganisationContext(value: string | number | null | undefined): boolean {
    return value == null || `${value}`.trim() === '' || `${value}`.trim() === '0';
  }
}

@Component({
  selector: 'app-password-reset-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, TranslatePipe],
  template: `
    <h2 mat-dialog-title>{{ 'passwordReset.title' | t }}</h2>
    <mat-dialog-content>
      <form class="reset-form" [formGroup]="form" (ngSubmit)="requestPasswordReset()">
        <p>{{ 'passwordReset.description' | t }}</p>

        <mat-form-field>
          <mat-label>{{ 'passwordReset.email' | t }}</mat-label>
          <input matInput type="email" autocomplete="email" formControlName="email" />
        </mat-form-field>

        @if (message()) {
          <div class="reset-message">{{ message() }}</div>
        }

        @if (errorMessage()) {
          <div class="reset-error">{{ errorMessage() }}</div>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [disabled]="loading()" mat-dialog-close>{{ 'common.cancel' | t }}</button>
      <button mat-flat-button color="primary" type="button" [disabled]="form.invalid || loading() || retrySeconds() > 0" (click)="requestPasswordReset()">
        @if (loading()) {
          <mat-spinner diameter="20" />
        } @else {
          <mat-icon>lock_reset</mat-icon>
        }
        @if (retrySeconds() > 0) { {{ 'oauth.passwordCompletion.retryIn' | t: { seconds: retrySeconds() } }} } @else { {{ 'passwordReset.send' | t }} }
      </button>
    </mat-dialog-actions>
  `,
  styles: `
    .reset-form {
      display: grid;
      gap: 12px;
      min-width: min(340px, calc(100vw - 64px));
      padding-top: 4px;
    }

    p {
      margin: 0;
      color: var(--app-text-muted);
      line-height: 1.35;
    }

    mat-form-field {
      width: 100%;
    }

    button[mat-flat-button] {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .reset-message,
    .reset-error {
      padding: 10px 12px;
      border-radius: 8px;
      font-weight: 700;
    }

    .reset-message {
      border: 1px solid #bbf7d0;
      color: #047857;
      background: #f0fdf4;
    }

    .reset-error {
      border: 1px solid #fecaca;
      color: #b91c1c;
      background: #fef2f2;
    }
  `
})
export class PasswordResetDialogComponent implements OnDestroy {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly auth = inject(AuthService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(false);
  readonly message = signal('');
  readonly errorMessage = signal('');
  readonly retrySeconds = signal(0);
  private retryTimer: ReturnType<typeof setInterval> | null = null;
  readonly form = this.fb.group({
    email: this.fb.control('', [Validators.required, Validators.email])
  });

  async requestPasswordReset(): Promise<void> {
    if (this.form.invalid || this.loading()) {
      return;
    }
    this.loading.set(true);
    this.message.set('');
    this.errorMessage.set('');

    try {
      const { email } = this.form.getRawValue();
      await this.auth.requestPasswordReset(email);
      this.message.set(this.localization.translate('passwordReset.success'));
      this.form.reset();
    } catch (error) {
      const retryAfter = authRetryAfterSeconds(error);
      if (retryAfter) {
        this.startRetryCountdown(retryAfter);
        this.errorMessage.set(this.localization.translate('auth.rateLimited', { seconds: retryAfter }));
      } else {
        this.errorMessage.set(this.localization.translate('passwordReset.error'));
      }
    } finally {
      this.loading.set(false);
    }
  }

  ngOnDestroy(): void { if (this.retryTimer) clearInterval(this.retryTimer); }

  private startRetryCountdown(seconds: number): void {
    if (this.retryTimer) clearInterval(this.retryTimer);
    this.retrySeconds.set(seconds);
    this.retryTimer = setInterval(() => {
      this.retrySeconds.update((value) => Math.max(0, value - 1));
      if (this.retrySeconds() === 0 && this.retryTimer) { clearInterval(this.retryTimer); this.retryTimer = null; }
    }, 1000);
  }
}
